// What to tell the person who pressed "Run the claim" on the SIL weekly tab, from the run's result
// (routes/silWeekly.js POST /run → utils/silWeekly.js runWeek): one line per participant, and the
// next step when a week is held for shift approval. A bare "Claimed 0, held 2" read as if nothing
// had happened.

const money = (n) => `$${Number(n || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const sentence = (s) => { const t = String(s || '').trim(); return t && !/[.!?]$/.test(t) ? `${t}.` : t; };
const lower1 = (s) => (s ? s[0].toLowerCase() + s.slice(1) : s);

function lineFor(r, min) {
  const who = r.client || 'A client';
  if (r.outcome === 'claimed') return `${who}: claimed ${money(r.amount)}.`;
  if (r.outcome === 'capped') return `${who}: claimed ${money(r.amount)}, ${lower1(sentence(r.reason || 'Capped at the funding left'))}`;
  if (r.outcome === 'already') return `${who}: already claimed.`;
  if (r.outcome === 'not_claimed') return `${who}: not claimed. ${sentence(r.reason || 'Nothing to claim this week')}`;
  if (r.outcome === 'held' && r.reason === 'Awaiting shift approval' && r.pending != null) {
    const approved = Number(r.approved) || 0;
    const total = approved + (Number(r.pending) || 0);
    const need = `${min} ${min === 1 ? 'is' : 'are'} needed`;
    return approved === 0
      ? `${who}: held. None of the ${total} SIL shifts this week has an approved timesheet yet; ${need}.`
      : `${who}: held. ${approved} of ${total} SIL shifts this week ${approved === 1 ? 'has' : 'have'} an approved timesheet; ${need}.`;
  }
  if (r.outcome === 'held') return `${who}: held. ${sentence(r.reason || 'Review before claiming')}`;
  return `${who}: ${r.outcome}.`;
}

export function describeRun(data, weekText) {
  const d = data || {};
  const results = Array.isArray(d.results) ? d.results : [];
  const errors = Array.isArray(d.errors) ? d.errors : [];
  const min = Math.max(1, Number(d.minApprovedShifts) || 1);
  const claimed = results.filter((r) => r.outcome === 'claimed' || r.outcome === 'capped');
  const held = results.filter((r) => r.outcome === 'held');
  const awaiting = held.filter((r) => r.reason === 'Awaiting shift approval');

  if (!results.length && !errors.length) {
    return {
      tone: 'slate', title: `Week ${weekText}: nobody to claim`, needsTimesheets: false, next: null,
      lines: ['No client in this organisation has SIL claimed weekly in this week. If you expected someone, check you are in the right organisation (top left), and that their SIL funding line is set to Weekly.'],
    };
  }
  const parts = [];
  if (claimed.length) parts.push(`${claimed.length} claimed (${money(claimed.reduce((a, r) => a + Number(r.amount || 0), 0))})`);
  if (held.length) parts.push(`${held.length} held`);
  const title = `Week ${weekText}: ${parts.length ? parts.join(', ') : 'nothing new to claim'}${held.length && !claimed.length ? ', nothing claimed yet' : ''}`;
  return {
    tone: errors.length || held.length ? 'amber' : claimed.length ? 'green' : 'slate',
    title,
    lines: [...results.map((r) => lineFor(r, min)), ...errors],
    needsTimesheets: awaiting.length > 0,
    next: awaiting.length
      ? 'Approve their timesheets in Timesheets, then run the claim again. Held weeks are also re-checked every day, so they are claimed by themselves once approved.'
      : null,
  };
}
